import { Box, CircularProgress, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header, { HEADER_HEIGHT } from "./general/header/Header";
import DefaultButton from "./general/buttons/DefaultButton";
import { verifyEmail } from "../services/user";
import { colors } from "../theme";

/**
 * Where a verification email lands. Public: the link is clicked from a mail client,
 * often in a browser with no session at all.
 *
 * Verifies on mount rather than behind a button - the click already happened, in the
 * email.
 */
export default function VerifyEmailPage() {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const [status, setStatus] = useState<"working" | "done" | "failed">("working");
    const [message, setMessage] = useState<string | null>(null);

    const run = useCallback(async () => {
        if (!token) {
            setStatus("failed");
            setMessage("This verification link is missing its token.");
            return;
        }
        try {
            const result = await verifyEmail(token);
            setMessage(result.message);
            setStatus("done");
        } catch (e) {
            setMessage(e instanceof Error ? e.message : "This verification link is no longer valid");
            setStatus("failed");
        }
    }, [token]);

    useEffect(() => {
        run();
    }, [run]);

    return (
        <Box sx={{ minHeight: "100vh", backgroundColor: colors.sidebar }}>
            <Header />
            <Box
                sx={{
                    minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 3,
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: 420,
                        p: 4,
                        borderRadius: 3,
                        backgroundColor: colors.elevated,
                        border: `1px solid ${colors.line}`,
                        textAlign: "center",
                    }}
                >
                    {status === "working" && <CircularProgress size={28} />}

                    {status === "done" && (
                        <>
                            <Typography variant="h5" sx={{ mb: 1 }}>Email confirmed</Typography>
                            <Typography sx={{ color: colors.textSecondary, fontSize: 14, mb: 3 }}>
                                {message}
                            </Typography>
                            <DefaultButton text="Go to your library" onClick={() => navigate("/dashboard")} />
                        </>
                    )}

                    {status === "failed" && (
                        <>
                            <Typography variant="h5" sx={{ mb: 1 }}>Link didn't work</Typography>
                            <Typography sx={{ color: colors.danger, fontSize: 14, mb: 3 }}>
                                {message}
                            </Typography>
                            <Typography sx={{ color: colors.textMuted, fontSize: 13, mb: 3 }}>
                                Verification links expire after 24 hours. You can request a fresh
                                one from Settings once you're signed in.
                            </Typography>
                            <DefaultButton text="Go to Shufl" onClick={() => navigate("/")} />
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
